/* Progressive formatting only. Values and links remain supplied product data. */
(() => {
  'use strict';
  if (customElements.get('saber-specification-layout') && customElements.get('saber-product-resources')) return;
  const blocks = new Set(['P', 'DIV', 'UL', 'OL', 'LI']);
  const inline = new Set(['A', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'SMALL', 'SUB', 'SUP', 'CODE', 'U', 'S', 'MARK']);
  const allowed = new Set(['BR', ...blocks, ...inline]);
  const hasLetters = value => /\p{L}/u.test(value);
  const folded = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const technicalHeading = value => /^(?:technical\s+specifications?|specifications?|specification\s+details|product\s+specifications?|dimensions?|measurements?|especificaciones?(?:\s+tecnicas?)?|caracteristicas?(?:\s+tecnicas?)?|dimensiones?|medidas)$/i.test(folded(value));
  // Declared product-property fields describe structure, not a finite vocabulary.
  // Outside that context, scalar values supply evidence for alternating records.
  const scalarValue = value => /^(?:yes|no|true|false|n\/a|not applicable|none|si|sí|no aplica|ninguno|ninguna)$/i.test(String(value).trim());
  const strongValue = value => scalarValue(value) || /^[-+]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:\s*(?:[-–×x/]\s*)?\d+(?:[.,]\d+)?)*(?:\s*(?:%|°[CF]?|[A-Za-zµ²³]{1,12}))?$/i.test(String(value).trim());
  const instructionOrSentence = value => /\b(?:is|are|was|were|has|have|can|should|must|will|may|please|this|these|your|our|the|its|it|es|son|tiene|tienen|puede|pueden|debe|deben|este|esta|estos|estas|su|sus|nuestro)\b/i.test(folded(value));
  function validLabel(label, declared = false) {
    const text = label.trim();
    return text.length > 0 && text.length <= 120 && hasLetters(text) && !scalarValue(text) && !/[.!?;]\s*$/.test(text)
      && (declared || !/\d\s*$/.test(text)) && !/\b(?:at|a\s+las)\s+\d+$/i.test(text)
      && !/^(?:https?|ftp|file|blob|mailto|tel|sms|urn|data|javascript)$/i.test(text)
      && !/^(?:[a-z][a-z0-9+.-]*:|\/|www\.)/i.test(text)
      && (declared || !/^(?:use|read|check|consult|follow|do not|keep|before|after|always|never|caution|warning|utilice|lea|consulte|siga|mantenga|antes|despues|siempre|nunca|precaucion|advertencia)\b/i.test(folded(text)));
  }
  function compactValue(value) {
    const text = folded(value);
    return text.length > 0 && text.length <= 160 && text.split(/\s+/).length <= 10
      && !/[!?]/.test(text)
      && !/^(?:use|read|check|consult|follow|see|do not|keep|before|after|always|never|caution|warning|utilice|lea|consulte|siga|vea|mantenga|antes|despues|siempre|nunca|precaucion|advertencia)\b/i.test(text)
      && !/\b(?:is|are|was|were|has|have|can|should|must|will|may|please|this|these|your|our|the|its|it|es|son|tiene|tienen|puede|pueden|debe|deben|este|esta|estos|estas|su|sus|nuestro)\b/i.test(text);
  }
  function linesFrom(value) {
    if (value.textContent.length > 100000 || value.querySelectorAll('*').length > 2000) return null;
    // A list followed by a citation or authored explanation has two distinct
    // semantic parts. Keep it intact instead of turning its source into a fact.
    if (value.querySelector('ul,ol')) {
      const content = value.ownerDocument.createTreeWalker(value, 4); let node;
      while ((node = content.nextNode())) if (node.nodeValue.trim() && !node.parentElement?.closest('li')) return null;
    }
    for (const element of value.querySelectorAll('*')) {
      if (!allowed.has(element.tagName)) return null;
      if (element.hasAttribute('id') || [...element.attributes].some(attribute => /^on/i.test(attribute.name))) return null;
    }
    const document = value.ownerDocument, lines = [];
    let fragment = document.createDocumentFragment(), text = '', copies = new Map();
    function flush() {
      if (text.trim()) lines.push({text, fragment});
      fragment = document.createDocumentFragment(); text = ''; copies = new Map();
    }
    function append(content, ancestors) {
      if (!content) return;
      let parent = fragment;
      for (const original of ancestors) {
        if (!copies.has(original)) { const copy = original.cloneNode(false); parent.append(copy); copies.set(original, copy); }
        parent = copies.get(original);
      }
      parent.append(document.createTextNode(content)); text += content;
    }
    function visit(node, ancestors = []) {
      if (node.nodeType === 3) {
        const pieces = node.nodeValue.replace(/\r\n?/g, '\n').split('\n');
        pieces.forEach((piece, index) => { if (index) flush(); append(piece, ancestors); });
        return;
      }
      if (node.nodeType !== 1) return;
      if (node.tagName === 'BR') { flush(); return; }
      if (blocks.has(node.tagName)) flush();
      const next = inline.has(node.tagName) ? [...ancestors, node] : ancestors;
      node.childNodes.forEach(child => visit(child, next));
      if (blocks.has(node.tagName)) flush();
    }
    value.childNodes.forEach(node => visit(node)); flush();
    return lines;
  }
  function slice(line, start, end = line.text.length) {
    const document = line.fragment.ownerDocument, walker = document.createTreeWalker(line.fragment, 4);
    const fragment = document.createDocumentFragment(), copies = new Map();
    let node, offset = 0;
    while ((node = walker.nextNode())) {
      const next = offset + node.nodeValue.length;
      if (start < next && end > offset) {
        const ancestors = []; let ancestor = node.parentElement;
        while (ancestor) { ancestors.unshift(ancestor); ancestor = ancestor.parentElement; }
        let parent = fragment;
        for (const original of ancestors) {
          if (!copies.has(original)) { const copy = original.cloneNode(false); parent.append(copy); copies.set(original, copy); }
          parent = copies.get(original);
        }
        parent.append(document.createTextNode(node.nodeValue.slice(Math.max(0, start - offset), Math.min(node.nodeValue.length, end - offset))));
      }
      if (end <= next) break;
      offset = next;
    }
    return fragment;
  }
  function normalize(value, heading, options = {}) {
    if (!value || value.querySelector('.ss-specification-pairs')) return null;
    const lines = linesFrom(value);
    if (!lines || !lines.length) return null;
    const pairs = [], declared = options.properties === true || technicalHeading(heading);
    const features = options.features === true, featureRecords = options.featureRecords === true;
    let alternating = false, strong = false;
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index], trimmed = line.text.trim(), colon = trimmed.indexOf(':');
      if (colon > 0 && validLabel(trimmed.slice(0, colon), true)) {
        const position = line.text.indexOf(':'), label = trimmed.slice(0, colon).trim();
        if (trimmed.slice(colon + 1).trim()) {
          const start = position + 1 + /^\s*/.exec(line.text.slice(position + 1))[0].length;
          pairs.push({label, labelContent: slice(line, line.text.indexOf(label), position), value: slice(line, start)});
          continue;
        }
        if (lines[index + 1] && /^([^:]+):\s*\S/.test(lines[index + 1].text.trim()) && validLabel(lines[index + 1].text.trim().split(':')[0], true)) return null;
        if (lines[index + 1]) {
          pairs.push({label, labelContent: slice(line, line.text.indexOf(label), position), value: lines[++index].fragment.cloneNode(true)});
          continue;
        }
      }
      if (!lines[index + 1]) {
        // A cropped or trailing label/note has no supplied value. Preserve it
        // after complete records without creating an invented empty/No value.
        if (pairs.length >= 2 && !strongValue(trimmed)) { pairs.notes = [line.fragment.cloneNode(true)]; break; }
        return null;
      }
      if (!validLabel(trimmed, declared) || (!declared && instructionOrSentence(trimmed))) return null;
      const next = lines[index + 1];
      if (/^([^:]+):\s*\S/.test(next.text.trim()) && validLabel(next.text.trim().split(':')[0], true)) return null;
      if ((!declared || (features && !featureRecords)) && !compactValue(next.text)) return null;
      strong ||= strongValue(next.text);
      alternating = true;
      pairs.push({label: trimmed, labelContent: line.fragment.cloneNode(true), value: lines[++index].fragment.cloneNode(true)});
    }
    // Features may be authored bullet lists rather than name/value records.
    // Numeric/boolean/N-A evidence distinguishes the reported property format;
    // explicit colon relationships do not require this additional inference.
    const authoredList = value.querySelector('ul,ol') || lines.some(line => /^\s*(?:[-*•]|\d+[.)])\s+/.test(line.text));
    if (alternating && (!declared || (features && !featureRecords) || authoredList) && !strong) return null;
    return pairs.length > 1 || declared ? pairs : null;
  }
  const gridSelector = '.ss-pdp-specification-grid,.ss-guide-specifications,.ss-pdp-quick-specs,.ss-pdp-feature-specs,[data-specification-grid]';
  function fieldIntent(row, grid, heading) {
    const source = row.getAttribute('data-reference-field-source') || row.getAttribute('data-reference-field') || row.dataset.specificationSource || '';
    const field = source.replace(/^(?:product|variant)\./, '');
    const features = /(?:^|\.)(?:features|product_features)$/.test(field)
      || grid.classList.contains('ss-pdp-feature-specs')
      || /^(?:features?(?:\s*&\s*highlights)?|product highlights|caracteristicas?(?:\s*y\s*aspectos\s*destacados)?)$/i.test(folded(heading));
    // A container can also hold scalar material/application text. Its broad
    // marker must not override an actual field's more specific source identity.
    const marked = row.dataset.specificationFormat === 'properties'
      || (!source && grid.dataset.specificationFormat === 'properties');
    return {features, featureRecords: /(?:^|\.)features$/.test(field) || row.dataset.specificationFormat === 'properties',
      properties: marked || /(?:^|\.)(?:specifications|technical_specifications|dimensions|measurements|features|product_features)$/.test(field)};
  }
  // Only the main Specifications surface opts into property-group disclosures.
  // Other uses of the same formatter remain fully expanded.
  const printStates = new Map();
  let printing = false;
  function openForPrint(disclosure) {
    if (!printStates.has(disclosure)) printStates.set(disclosure, disclosure.open);
    disclosure.open = true;
  }
  window.addEventListener('beforeprint', () => {
    printing = true;
    for (const disclosure of document.querySelectorAll('[data-specification-collapse-long] details[data-specification-disclosure]')) openForPrint(disclosure);
  });
  window.addEventListener('afterprint', () => {
    for (const [disclosure, wasOpen] of printStates) if (disclosure.isConnected) disclosure.open = wasOpen;
    printStates.clear(); printing = false;
  });
  function collapseLongGroup(row, grid, pairs, term, content) {
    // The shared product template already supplies native group disclosures,
    // including prose and no-JavaScript output. Never create a nested toggle.
    if (row.closest('details[data-specification-disclosure]') || !grid.closest('[data-specification-collapse-long]') || !term.textContent.trim()) return content;
    const source = row.getAttribute('data-reference-field-source') || row.getAttribute('data-reference-field') || row.dataset.specificationSource || '';
    const key = source.split('.').pop();
    const heading = folded(term.textContent);
    if (/^(?:dimensions|measurements)$/.test(key) || /^(?:dimensions?|measurements?|dimensiones?|medidas)$/i.test(heading)) return content;
    const propertyGroup = /^(?:specifications|technical_specifications|features|product_features)$/.test(key);
    const namedGroup = technicalHeading(heading) || /^(?:features?(?:\s*&\s*highlights)?|product highlights|caracteristicas?(?:\s*y\s*aspectos\s*destacados)?)$/i.test(heading);
    if (!propertyGroup && !namedGroup) return content;
    const disclosure = row.ownerDocument.createElement('details'), summary = row.ownerDocument.createElement('summary');
    disclosure.className = 'ss-specification-disclosure'; disclosure.dataset.specificationDisclosure = '';
    summary.textContent = term.textContent.trim(); disclosure.append(summary, ...content);
    term.classList.add('visually-hidden'); row.classList.add('ss-specification-row--collapsible');
    if (printing) openForPrint(disclosure);
    return [disclosure];
  }
  function format(container) {
    const result = {structured: 0, unchanged: 0};
    const grids = [...container.querySelectorAll(gridSelector)];
    if (container.matches?.(gridSelector)) grids.unshift(container);
    for (const grid of grids) for (const row of grid.children) {
      if (!row.matches('.ss-specification-row,[data-specification-record]') || row.dataset.specificationFormatted === 'true') continue;
      const term = row.querySelector(':scope > dt'), value = row.querySelector(':scope > dd');
      if (!term || !value) continue;
      row.dataset.specificationFormatted = 'true';
      const pairs = normalize(value, term.textContent, fieldIntent(row, grid, term.textContent));
      if (!pairs) {
        if (value.querySelector('table') || value.textContent.trim().length > 240 || value.querySelectorAll('br,li,p,tr').length >= 3) row.classList.add('ss-specification-row--long');
        result.unchanged++; continue;
      }
      const details = row.ownerDocument.createElement('dl'); details.className = 'ss-specification-pairs';
      details.setAttribute('aria-label', term.textContent.trim());
      for (const supplied of pairs) {
        const pair = row.ownerDocument.createElement('div'), key = row.ownerDocument.createElement('dt'), item = row.ownerDocument.createElement('dd');
        pair.className = 'ss-specification-pair'; key.append(supplied.labelContent); item.append(supplied.value);
        pair.append(key, item); details.append(pair);
      }
      const content = [details];
      if (pairs.notes?.length) {
        const notes = row.ownerDocument.createElement('div'); notes.className = 'ss-specification-notes';
        for (const supplied of pairs.notes) { const note = row.ownerDocument.createElement('p'); note.append(supplied); notes.append(note); }
        content.push(notes);
      }
      value.replaceChildren(...collapseLongGroup(row, grid, pairs, term, content)); row.classList.add('ss-specification-row--structured'); result.structured++;
    }
    // A selected-variant refresh can insert native groups while a print dialog
    // is preparing. Include those groups and restore their own original states.
    if (printing) for (const disclosure of container.querySelectorAll('details[data-specification-disclosure]')) {
      if (disclosure.closest('[data-specification-collapse-long]')) openForPrint(disclosure);
    }
    return result;
  }
  window.SaberSpecificationLayout = Object.freeze({normalize, format});
  class SpecificationLayout extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.dataset.specificationReady === 'true') return;
        const complete = () => { this._referenceWait?.disconnect(); this._referenceWait = null; format(this); this.dataset.specificationReady = 'true'; };
        if (this.hasAttribute('data-reference-layout') && this.ownerDocument.body.hasAttribute('data-reference-sheet') && !/^(?:complete|incomplete)$/.test(this.ownerDocument.body.dataset.guideDetailsState || '')) {
          this._referenceWait?.disconnect();
          this._referenceWait = new MutationObserver(() => {
            if (this.isConnected && /^(?:complete|incomplete)$/.test(this.ownerDocument.body.dataset.guideDetailsState || '')) complete();
          });
          this._referenceWait.observe(this.ownerDocument.body, {attributes: true, attributeFilter: ['data-guide-details-state']});
          return;
        }
        complete();
      });
    }
    disconnectedCallback() { this._referenceWait?.disconnect(); this._referenceWait = null; }
  }
  class ProductResources extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.dataset.resourcesReady === 'true') return;
        this.dataset.resourcesReady = 'true';
        const seen = new Set();
        for (const anchor of this.querySelectorAll('a[href]')) {
          const url = anchor.href;
          if (seen.has(url)) {
            const item = anchor.closest('li');
            const parent = anchor.parentElement;
            if (item && this.contains(item) && item.children.length === 1 && item.firstElementChild === anchor && item.textContent.trim() === anchor.textContent.trim()) { item.remove(); continue; }
            if (parent?.tagName === 'DD' && parent.children.length === 1 && parent.firstElementChild === anchor && parent.textContent.trim() === anchor.textContent.trim()) { anchor.remove(); continue; }
            // An inline/prose link can carry authored context; keep its text and
            // destination even if another resource uses the same URL.
          }
          seen.add(url);
          if (anchor.children.length) continue;
          const label = this.ownerDocument.createElement('span');
          label.textContent = anchor.textContent.trim().replace(/\s*[↗→]\s*$/, '').trim();
          const icon = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg'), path = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
          icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('width', '16'); icon.setAttribute('height', '16'); icon.setAttribute('fill', 'none'); icon.setAttribute('stroke', 'currentColor'); icon.setAttribute('stroke-width', '1.8'); icon.setAttribute('aria-hidden', 'true');
          path.setAttribute('d', anchor.target === '_blank' ? 'M8 5H5v14h14v-3M13 5h6v6M10 14l9-9' : 'M5 12h14m-5-5l5 5-5 5');
          icon.append(path); anchor.replaceChildren(label, icon);
        }
        for (const row of this.querySelectorAll('.ss-pdp-documents > .ss-specification-row')) {
          const value = row.querySelector(':scope > dd');
          if (value && !value.textContent.trim() && !value.querySelector('a,img')) row.remove();
        }
        for (const list of this.querySelectorAll('.ss-pdp-documents')) if (!list.children.length) list.hidden = true;
      });
    }
  }
  if (!customElements.get('saber-specification-layout')) customElements.define('saber-specification-layout', SpecificationLayout);
  if (!customElements.get('saber-product-resources')) customElements.define('saber-product-resources', ProductResources);
})();
